import React, { useState, useEffect } from 'react';
import { X, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { localizeOutcome } from '../lib/outcomes';

const CALL_OUTCOMES = [
  'Primo Deposito Effettuato',
  'Appuntamento',
  'Richiamo',
  'Non Interessato',
  'Non Risponde',
  'Segreteria',
  'Numero Errato',
  'Numero Inesistente',
  'Occupato',
  'Cliente già registrato',
  'Cliente indeciso',
  'Cliente richiede informazioni',
  'Da ricontattare',
  'Chiamata interrotta',
  'Cliente non idoneo',
];

const CallModal = ({ lead, startTime, onClose, onSubmit, theme }) => {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [duration, setDuration] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    const interval = setInterval(() => {
      if (startTime) {
        const elapsed = Math.floor((new Date() - startTime) / 1000);
        setDuration(elapsed);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = () => {
    if (!outcome) {
      toast.error(t('outcome_required'));
      return;
    }
    if (!note.trim()) {
      toast.error(t('note_required'));
      return;
    }
    onSubmit(outcome, note);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      data-testid="call-modal"
    >
      <div
        className="w-full max-w-md rounded-lg p-6 shadow-xl slide-in-right"
        style={{
          backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
          border: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}`,
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2
              className="text-2xl font-bold"
              style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
              data-testid="call-modal-title"
            >
              {t('call_in_progress')}
            </h2>
            <p className="text-sm mt-1" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
              {lead.first_name} {lead.last_name}
            </p>
          </div>
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-md call-timer-pulse"
            style={{ backgroundColor: '#FA4D5615' }}
          >
            <Phone size={16} style={{ color: '#FA4D56' }} />
            <span className="font-mono text-lg font-bold" style={{ color: '#FA4D56' }}>
              {formatDuration(duration)}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="outcome" data-testid="call-outcome-label">
              {t('call_outcome')} *
            </Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="mt-1" data-testid="call-outcome-select">
                <SelectValue placeholder="..." />
              </SelectTrigger>
              <SelectContent>
                {CALL_OUTCOMES.map((out) => (
                  <SelectItem key={out} value={out}>
                    {localizeOutcome(t, out)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="note" data-testid="call-note-label">
              {t('call_note')} *
            </Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="..."
              rows={4}
              className="mt-1"
              data-testid="call-note-textarea"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              className="flex-1 btn-primary"
              onClick={handleSubmit}
              data-testid="call-submit-button"
            >
              {t('call_save')}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="call-cancel-button"
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallModal;
